/**
 * Google Analytics tracking utility
 * Provides reusable functions for tracking events with cookie consent checking
 */
const { GA_MEASUREMENT_ID } = require("../config/analytics")

/**
 * Google Ads tracking utility
 * Captures and stores Google Ads tracking parameters
 */

/**
 * Extract Google Ads tracking parameters from URL
 * @returns {Object} Object containing Google Ads tracking parameters
 */
export const extractGoogleAdsParams = () => {
  if (typeof window === "undefined") return {}

  const urlParams = new URLSearchParams(window.location.search)
  const googleAdsParams = {}

  // Google Ads click identifier
  const gclid = urlParams.get("gclid")
  if (gclid) googleAdsParams.gclid = gclid

  // Google Ads source
  const gclsrc = urlParams.get("gclsrc")
  if (gclsrc) googleAdsParams.gclsrc = gclsrc

  // Keep each provider's latest click reference for the app/auth handoff.
  for (const key of ["msclkid", "oppref"]) {
    const value = urlParams.get(key)
    if (value && value.length <= 2048 && !/\s/.test(value)) {
      googleAdsParams[key] = value
    }
  }

  const linkedInClickId = urlParams.get("li_fat_id")
  if (
    linkedInClickId &&
    /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(linkedInClickId)
  ) {
    googleAdsParams.li_fat_id = linkedInClickId
  }

  // UTM parameters
  const utmSource = urlParams.get("utm_source")
  if (utmSource) googleAdsParams.utm_source = utmSource

  const utmMedium = urlParams.get("utm_medium")
  if (utmMedium) googleAdsParams.utm_medium = utmMedium

  const utmCampaign = urlParams.get("utm_campaign")
  if (utmCampaign) googleAdsParams.utm_campaign = utmCampaign

  const utmTerm = urlParams.get("utm_term")
  if (utmTerm) googleAdsParams.utm_term = utmTerm

  const utmContent = urlParams.get("utm_content")
  if (utmContent) googleAdsParams.utm_content = utmContent

  // Google Ads Dynamic parameters (gad_*)
  const gadSource = urlParams.get("gad_source")
  if (gadSource) googleAdsParams.gad_source = gadSource

  const gadMedium = urlParams.get("gad_medium")
  if (gadMedium) googleAdsParams.gad_medium = gadMedium

  const gadCampaignId = urlParams.get("gad_campaignid")
  if (gadCampaignId) googleAdsParams.gad_campaignid = gadCampaignId

  const gadTerm = urlParams.get("gad_term")
  if (gadTerm) googleAdsParams.gad_term = gadTerm

  const gadContent = urlParams.get("gad_content")
  if (gadContent) googleAdsParams.gad_content = gadContent

  // Custom Klang tracking ID
  const klangTrackingId = urlParams.get("klang_trackingid")
  if (klangTrackingId) googleAdsParams.klang_trackingid = klangTrackingId

  return googleAdsParams
}

/**
 * Store Google Ads tracking parameters in sessionStorage
 * @param {Object} params - Google Ads tracking parameters
 */
export const storeGoogleAdsParams = params => {
  if (typeof window === "undefined") return

  const hasParams = Object.keys(params).length > 0
  if (hasParams) {
    try {
      const attribution = { ...getGoogleAdsParams(), ...params }
      sessionStorage.setItem("googleAdsParams", JSON.stringify(attribution))
    } catch {
      // Attribution degrades in restricted storage contexts; the page must not.
    }
  }
}

/**
 * Retrieve Google Ads tracking parameters from sessionStorage
 * @returns {Object} Stored Google Ads tracking parameters
 */
export const getGoogleAdsParams = () => {
  if (typeof window === "undefined") return {}

  try {
    const stored = sessionStorage.getItem("googleAdsParams")
    return stored ? JSON.parse(stored) : {}
  } catch (error) {
    console.warn("Failed to parse stored Google Ads parameters:", error)
    return {}
  }
}

/**
 * Clear stored Google Ads tracking parameters
 */
export const clearGoogleAdsParams = () => {
  if (typeof window === "undefined") return
  sessionStorage.removeItem("googleAdsParams")
}

const GA_SESSION_COOKIE = "klang_ga_session"

function gaSessionCookieAttributes(maxAge) {
  const crossSubdomain =
    window.location.hostname === "klang.ai" ||
    window.location.hostname.endsWith(".klang.ai")
  return [
    `Max-Age=${maxAge}`,
    "Path=/",
    "SameSite=Lax",
    ...(crossSubdomain ? ["Domain=.klang.ai", "Secure"] : []),
  ].join("; ")
}

function clearGaSessionCookie() {
  document.cookie = `${GA_SESSION_COOKIE}=; ${gaSessionCookieAttributes(0)}`
}

/**
 * Cache GA4's current session id in a short-lived first-party cookie shared
 * with auth. This lets server-side conversions retain session attribution.
 */
export const refreshGaSessionCookie = () => {
  if (typeof window === "undefined") return
  if (!window.Cookiebot?.consent?.statistics || !window.gtag) {
    clearGaSessionCookie()
    return
  }

  window.gtag("get", GA_MEASUREMENT_ID, "session_id", sessionId => {
    if (!window.Cookiebot?.consent?.statistics) return
    const value = String(sessionId ?? "")
    if (!/^\d{9,10}$/.test(value) || Number(value) <= 0) return
    document.cookie =
      `${GA_SESSION_COOKIE}=${encodeURIComponent(value)}; ` +
      gaSessionCookieAttributes(30 * 60)
  })
}

export const initGaSessionHandoff = () => {
  if (typeof window === "undefined") return
  refreshGaSessionCookie()
  window.addEventListener("CookiebotOnAccept", refreshGaSessionCookie)
  window.addEventListener("CookiebotOnDecline", clearGaSessionCookie)
}

/**
 * Initialize Google Ads tracking on page load
 * Extracts parameters from URL and stores them in sessionStorage
 */
export const initGoogleAdsTracking = () => {
  if (typeof window === "undefined") return

  const params = extractGoogleAdsParams()
  if (Object.keys(params).length > 0) {
    storeGoogleAdsParams(params)
  }
}

/**
 * Track a custom event if analytics consent is granted
 * @param {string} eventName - The name of the event
 * @param {string} eventCategory - The category of the event (defaults to eventName)
 * @param {string} eventLabel - The label of the event (defaults to eventName)
 * @param {Object} additionalParams - Additional parameters to send with the event
 */
export const trackEvent = (
  eventName,
  eventCategory = eventName,
  eventLabel = eventName,
  additionalParams = {}
) => {
  if (
    typeof window !== "undefined" &&
    window.gtag &&
    window.Cookiebot &&
    window.Cookiebot.consent.statistics
  ) {
    window.gtag("event", eventName, {
      event_category: eventCategory,
      event_label: eventLabel,
      ...additionalParams,
    })
  }
}

/**
 * Track a CTA click on an organic or ad landing page. Each CTA carries
 * data-cta-position (top | mid | end | sticky, …) and data-cta-target
 * (signup | app_store | google_play | product | template, …).
 * @param {MouseEvent} event - A click anywhere inside the page
 * @param {string} pageType - e.g. "phone_guide", "research_guide", "ad_lp"
 * @param {Object} extraParams - Sent with the event, e.g. { lp: "lp-<slug>" }
 */
export const trackCtaClick = (event, pageType, extraParams = {}) => {
  const anchor =
    event.target.closest && event.target.closest("[data-cta-target]")
  if (!anchor) return
  const position = anchor.getAttribute("data-cta-position") || ""
  const target = anchor.getAttribute("data-cta-target") || ""
  trackEvent("cta_click", "cta_click", target, {
    position,
    target,
    page_type: pageType,
    page_path: window.location.pathname,
    ...extraParams,
  })
}

/**
 * Track button clicks with automatic data-tag detection
 * @param {Event} event - The click event
 */
export const trackButtonClick = event => {
  const tag = event.target.getAttribute("data-tag")
  if (tag) {
    trackEvent(tag, tag, tag)
  }
}

/**
 * Get the appropriate data-tag for a URL
 * @param {string} href - The URL to analyze
 * @returns {string} The appropriate data-tag for tracking
 */
export const getDataTag = href => {
  if (href.includes("app.klang.ai/signup")) {
    return "signup_button"
  } else if (href.includes("/contact") || href.includes("/demo")) {
    return "demo_button"
  } else if (href.includes("app.klang.ai")) {
    return "external_klang_link"
  } else {
    return "generic_button"
  }
}

/**
 * Track specific event types
 */
export const trackSignupButton = () => {
  trackEvent("signup_button", "signup_button", "signup_button")
}

export const trackDemoButton = () => {
  trackEvent("demo_button", "demo_button", "demo_button")
}

export const trackExternalKlangLink = () => {
  trackEvent(
    "external_klang_link",
    "external_klang_link",
    "external_klang_link"
  )
}
